<script lang="ts">
  // Two layouts over the same question model:
  //  - 'wizard' (default): one question at a time with Next/Back — the
  //    original flow, right for short branching intakes.
  //  - 'form': every visible question on one page, grouped by `section`
  //    headers, with a live progress bar — right for long applications
  //    (vidamor uses this).
  // Conditions (showIf) work in both: answering reveals/hides live.
  import { createEventDispatcher, tick } from 'svelte';
  import QuestionInput from './QuestionInput.svelte';
  import type { AnswerValue, QuestionDefinition } from '../../lib/types';

  export let questions: QuestionDefinition[] = [];
  export let submitLabel = 'Submit';
  export let initialAnswers: Record<string, AnswerValue> = {};
  export let draftKey: string | null = null;
  export let busy = false;
  export let layout: 'wizard' | 'form' = 'wizard';

  const dispatch = createEventDispatcher<{
    submit: Record<string, AnswerValue>;
    cancel: void;
  }>();

  let answers: Record<string, AnswerValue> = loadDraft() ?? { ...initialAnswers };
  let currentIndex = 0;
  let validationError: string | null = null;
  let fieldErrors: Record<string, string> = {};
  let uploading = false;

  // Only the questions whose showIf condition is met (conditions always point
  // to earlier questions, so one forward pass resolves chains).
  function computeVisible(
    all: QuestionDefinition[],
    current: Record<string, AnswerValue>
  ): QuestionDefinition[] {
    const visibleIds = new Set<string>();
    const result: QuestionDefinition[] = [];
    for (const question of all) {
      const condition = question.showIf;
      if (condition) {
        if (!visibleIds.has(condition.questionId)) continue;
        if (current[condition.questionId] !== condition.equals) continue;
      }
      visibleIds.add(question.id);
      result.push(question);
    }
    return result;
  }

  $: visible = computeVisible(questions, answers);
  $: totalQuestions = visible.length;
  $: activeQuestion = visible[Math.min(currentIndex, Math.max(totalQuestions - 1, 0))];
  $: isLast = currentIndex >= totalQuestions - 1;

  function isAnswered(question: QuestionDefinition): boolean {
    const value = answers[question.id];
    return !(
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    );
  }

  // Form-layout progress: answered / visible. Optional questions count too —
  // the bar measures how far down the form you are, not eligibility.
  $: answeredCount = visible.filter(isAnswered).length;

  function loadDraft(): Record<string, AnswerValue> | null {
    if (!draftKey || typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  function persistDraft() {
    if (!draftKey || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(draftKey, JSON.stringify(answers));
    } catch {
      // localStorage full/blocked — drafts are best-effort only
    }
  }

  export function clearDraft() {
    if (!draftKey || typeof localStorage === 'undefined') return;
    try {
      localStorage.removeItem(draftKey);
    } catch {
      // ignore
    }
  }

  function setAnswer(id: string, value: AnswerValue | undefined) {
    if (value === undefined) {
      const next = { ...answers };
      delete next[id];
      answers = next;
    } else {
      answers = { ...answers, [id]: value };
    }
    validationError = null;
    if (fieldErrors[id]) {
      const next = { ...fieldErrors };
      delete next[id];
      fieldErrors = next;
    }
    persistDraft();
  }

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function validate(question: QuestionDefinition, value: AnswerValue | undefined): string | null {
    const missing =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);
    if (missing) {
      return question.required ? 'This question is required.' : null;
    }
    switch (question.type) {
      case 'email':
        return EMAIL_PATTERN.test(String(value).trim()) ? null : 'Enter a valid email address.';
      case 'url': {
        const raw = String(value).trim();
        try {
          new URL(raw.includes('://') ? raw : `https://${raw}`);
          return null;
        } catch {
          return 'Enter a valid link.';
        }
      }
      case 'number':
      case 'slider': {
        const num = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(num)) return 'Enter a number.';
        if (question.min !== undefined && num < question.min) return `Must be at least ${question.min}.`;
        if (question.max !== undefined && num > question.max) return `Must be at most ${question.max}.`;
        return null;
      }
      default:
        return null;
    }
  }

  function submitVisible() {
    // Only submit answers to visible questions — flipping a yes/no back and
    // forth must not leak answers from the branch that ended up hidden.
    const submitted: Record<string, AnswerValue> = {};
    for (const question of visible) {
      if (answers[question.id] !== undefined) submitted[question.id] = answers[question.id];
    }
    dispatch('submit', submitted);
  }

  // --- wizard navigation ------------------------------------------------------
  function goNext() {
    if (!activeQuestion) return;
    const error = validate(activeQuestion, answers[activeQuestion.id]);
    if (error) {
      validationError = error;
      return;
    }
    persistDraft();
    if (isLast) {
      submitVisible();
      return;
    }
    currentIndex += 1;
    validationError = null;
  }

  function goBack() {
    if (currentIndex === 0) {
      dispatch('cancel');
      return;
    }
    persistDraft();
    currentIndex -= 1;
    validationError = null;
  }

  // --- form submit: validate everything, jump to the first problem -------------
  async function submitForm() {
    const errors: Record<string, string> = {};
    for (const question of visible) {
      const error = validate(question, answers[question.id]);
      if (error) errors[question.id] = error;
    }
    fieldErrors = errors;
    const firstErrorId = visible.find(question => errors[question.id])?.id;
    if (firstErrorId) {
      await tick();
      document
        .querySelector(`[data-question-id="${firstErrorId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    persistDraft();
    submitVisible();
  }

  /** The section header to show above a question: its section, when it differs
   * from the previous visible question's. */
  function sectionHeaderFor(index: number): string | null {
    const section = visible[index]?.section ?? null;
    if (!section) return null;
    const previous = index > 0 ? visible[index - 1]?.section ?? null : null;
    return section === previous ? null : section;
  }
</script>

<div class="qr">
  {#if totalQuestions === 0}
    <p class="qr__empty">This questionnaire has no questions yet.</p>
  {:else if layout === 'form'}
    <div class="qr__progress qr__progress--sticky">
      <span class="qr__progress-label">{answeredCount} of {totalQuestions} answered</span>
      <div class="qr__progress-bar">
        <div class="qr__progress-fill" style={`width: ${(answeredCount / totalQuestions) * 100}%`}></div>
      </div>
    </div>

    {#each visible as question, index (question.id)}
      {@const header = sectionHeaderFor(index)}
      {#if header}
        <p class="qr__section">{header}</p>
      {/if}
      <div class="qr__question" data-question-id={question.id}>
        <p class="qr__prompt">
          {question.prompt}
          {#if !question.required}
            <span class="qr__optional">Optional</span>
          {/if}
        </p>
        {#if question.helper}
          <p class="qr__helper">{question.helper}</p>
        {/if}
        <QuestionInput
          {question}
          value={answers[question.id]}
          on:answer={event => setAnswer(question.id, event.detail)}
          on:uploading={event => (uploading = event.detail)}
        />
        {#if fieldErrors[question.id]}
          <p class="qr__error">{fieldErrors[question.id]}</p>
        {/if}
      </div>
    {/each}

    <div class="qr__nav">
      <button type="button" class="ghost" on:click={() => dispatch('cancel')} disabled={busy || uploading}>
        Cancel
      </button>
      <button type="button" class="accent" on:click={submitForm} disabled={busy || uploading}>
        {busy ? 'Sending…' : uploading ? 'Uploading…' : submitLabel}
      </button>
    </div>
  {:else}
    <div class="qr__progress">
      <span class="qr__progress-label">
        Question {Math.min(currentIndex + 1, totalQuestions)} of {totalQuestions}
      </span>
      <div class="qr__progress-bar">
        <div
          class="qr__progress-fill"
          style={`width: ${((Math.min(currentIndex + 1, totalQuestions)) / totalQuestions) * 100}%`}
        ></div>
      </div>
    </div>

    {#key activeQuestion.id}
      <div class="qr__question">
        <p class="qr__prompt">
          {activeQuestion.prompt}
          {#if !activeQuestion.required}
            <span class="qr__optional">Optional</span>
          {/if}
        </p>
        {#if activeQuestion.helper}
          <p class="qr__helper">{activeQuestion.helper}</p>
        {/if}

        <QuestionInput
          question={activeQuestion}
          value={answers[activeQuestion.id]}
          on:answer={event => setAnswer(activeQuestion.id, event.detail)}
          on:uploading={event => (uploading = event.detail)}
        />

        {#if validationError}
          <p class="qr__error">{validationError}</p>
        {/if}
      </div>
    {/key}

    <div class="qr__nav">
      <button type="button" class="ghost" on:click={goBack} disabled={busy || uploading}>
        {currentIndex === 0 ? 'Cancel' : 'Back'}
      </button>
      <button type="button" class="accent" on:click={goNext} disabled={busy || uploading}>
        {busy && isLast ? 'Sending…' : uploading ? 'Uploading…' : isLast ? submitLabel : 'Next'}
      </button>
    </div>
  {/if}
</div>

<style>
  .qr {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .qr__empty {
    margin: 0;
    color: var(--color-text-muted);
  }

  .qr__progress {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  /* Long form: the bar rides along while scrolling. */
  .qr__progress--sticky {
    position: sticky;
    top: 0;
    z-index: 5;
    padding: var(--space-2) 0;
    background: inherit;
  }

  .qr__progress-label {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .qr__progress-bar {
    height: 4px;
    border-radius: var(--radius-pill);
    background: var(--color-surface-850);
    overflow: hidden;
  }

  .qr__progress-fill {
    height: 100%;
    background: var(--color-text-amber);
    border-radius: var(--radius-pill);
    transition: width 0.25s ease;
  }

  .qr__section {
    margin: var(--space-3) 0 0;
    font-size: var(--font-size-xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--color-text-muted);
    border-bottom: var(--border-subtle);
    padding-bottom: var(--space-2);
  }

  .qr__question {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .qr__prompt {
    margin: 0;
    font-weight: 600;
    line-height: var(--line-height-relaxed);
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .qr__optional {
    font-size: var(--font-size-2xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-text-muted);
    border: var(--border-subtle);
    border-radius: var(--radius-pill);
    padding: 1px var(--space-2);
  }

  .qr__helper {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
  }

  .qr__error {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-danger-400, #ff7676);
  }

  .qr__nav {
    display: flex;
    justify-content: space-between;
    gap: var(--space-3);
  }
</style>
